import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Clock, Users, Star, ChevronRight, Medal, Flame, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { useChallenges } from '@/hooks/useChallenges';
import { useCloset } from '@/hooks/useCloset';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import { cn } from '@/lib/utils';

interface ChallengesViewProps {
  isNightMode?: boolean;
}

export function ChallengesView({ isNightMode }: ChallengesViewProps) {
  const { challenges, entries, leaderboard, loading, fetchEntries, submitEntry, voteForEntry } = useChallenges();
  const { items: closetItems } = useCloset();
  const { user } = useAuth();
  const [selectedChallenge, setSelectedChallenge] = useState<string | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [caption, setCaption] = useState('');

  const activeChallenge = challenges.find(c => c.id === selectedChallenge);

  useEffect(() => {
    if (selectedChallenge) {
      fetchEntries(selectedChallenge);
    }
  }, [selectedChallenge, fetchEntries]);

  const handleSubmitEntry = async () => {
    if (!selectedChallenge || selectedItems.length === 0) return;

    const outfitData = {
      items: selectedItems.map(id => {
        const item = closetItems.find(i => i.id === id);
        return {
          id: item?.id || id,
          name: item?.name || 'Item',
          image_url: item?.image_url || null,
          type: item?.type || 'unknown'
        };
      })
    };

    const success = await submitEntry(selectedChallenge, outfitData, caption);
    if (success) {
      setShowSubmit(false);
      setSelectedItems([]);
      setCaption('');
    }
  };

  const getTimeLeft = (endDate: string) => {
    const hours = differenceInHours(new Date(endDate), new Date());
    if (hours < 24) return `${hours}h left`;
    return `${Math.floor(hours / 24)}d left`;
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-muted-foreground text-sm">#{index + 1}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-bold text-foreground">Style Challenges</h2>
        </div>
        {selectedChallenge && (
          <Button variant="ghost" onClick={() => setSelectedChallenge(null)}>
            All Challenges
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!selectedChallenge ? (
          /* Challenge List */
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid gap-4"
          >
            {challenges.length === 0 ? (
              <Card className="p-8 text-center border-dashed">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-aura-gold/20 to-aura-rose/20 flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-aura-gold" />
                </div>
                <h3 className="text-lg font-serif mb-2 text-foreground">No Active Challenges</h3>
                <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                  New style challenges are coming soon! Check back to compete with the community and win Aura Points.
                </p>
              </Card>
            ) : (
              challenges.map((challenge, idx) => (
                <motion.div
                  key={challenge.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card 
                    className="p-4 cursor-pointer hover:shadow-lg transition-all border-border hover:border-primary/50"
                    onClick={() => setSelectedChallenge(challenge.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <Flame className="w-8 h-8 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-foreground">{challenge.title}</h3>
                          <Badge variant="secondary" className="text-xs">
                            {challenge.theme}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {challenge.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getTimeLeft(challenge.end_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {challenge.entries_count} entries
                          </span>
                          <span className="flex items-center gap-1 text-primary font-medium">
                            <Star className="w-3 h-3" />
                            {challenge.prize_points} pts
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        ) : (
          /* Challenge Detail */
          <motion.div
            key="detail"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Challenge Info */}
            <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-foreground">{activeChallenge?.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{activeChallenge?.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {activeChallenge && getTimeLeft(activeChallenge.end_date)}
                  </span>
                  <span className="flex items-center gap-1 text-primary font-semibold">
                    <Award className="w-4 h-4" />
                    Win {activeChallenge?.prize_points} Aura Points
                  </span>
                </div>
                <Button onClick={() => setShowSubmit(true)} size="sm">
                  Enter Challenge
                </Button>
              </div>
            </Card>

            {/* Leaderboard */}
            <Card className="p-4">
              <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-primary" />
                Leaderboard
              </h4>
              <div className="space-y-3">
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">No entries yet</p>
                    <p className="text-xs text-muted-foreground/70">Be the first to enter this challenge!</p>
                  </div>
                ) : (
                  leaderboard.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-xl transition-colors",
                        idx === 0 && "bg-yellow-500/10",
                        idx === 1 && "bg-gray-400/10",
                        idx === 2 && "bg-amber-600/10"
                      )}
                    >
                      <div className="w-8 flex justify-center">
                        {getRankIcon(idx)}
                      </div>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={entry.user_profile?.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {entry.user_profile?.display_name?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {entry.user_profile?.display_name || 'Stylist'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.user_profile?.aura_level || 'Initiate'}
                        </p>
                      </div>
                      <Button
                        variant={entry.has_voted ? "default" : "outline"}
                        size="sm"
                        onClick={() => voteForEntry(entry.id, selectedChallenge)}
                        disabled={entry.user_id === user?.id}
                        className="gap-1"
                      >
                        <Star className={cn("w-3 h-3", entry.has_voted && "fill-current")} />
                        {entry.votes_count}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* All Entries Grid */}
            <div>
              <h4 className="font-semibold text-foreground mb-4">All Entries</h4>
              <div className="grid grid-cols-2 gap-3">
                {entries.map((entry) => (
                  <Card key={entry.id} className="overflow-hidden">
                    <div className="aspect-square bg-muted relative">
                      {entry.outfit_data.items[0]?.image_url ? (
                        <img
                          src={entry.outfit_data.items[0].image_url}
                          alt="Outfit"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl">👔</span>
                        </div>
                      )}
                      <Badge className="absolute top-2 right-2 bg-black/60">
                        {entry.outfit_data.items.length} items
                      </Badge>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={entry.user_profile?.avatar_url || undefined} />
                          <AvatarFallback className="text-xs bg-primary/20 text-primary">
                            {entry.user_profile?.display_name?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-foreground truncate">
                          {entry.user_profile?.display_name || 'Stylist'}
                        </span>
                      </div>
                      {entry.caption && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{entry.caption}</p>
                      )}
                      <Button
                        variant={entry.has_voted ? "default" : "outline"}
                        size="sm"
                        className="w-full gap-1"
                        onClick={() => voteForEntry(entry.id, selectedChallenge)}
                        disabled={entry.user_id === user?.id}
                      >
                        <Star className={cn("w-3 h-3", entry.has_voted && "fill-current")} />
                        Vote ({entry.votes_count})
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Entry Modal */}
      <AnimatePresence>
        {showSubmit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
            onClick={() => setShowSubmit(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden"
            >
              <div className="p-4 border-b border-border">
                <h3 className="font-bold text-lg text-foreground">Submit Your Look</h3>
                <p className="text-sm text-muted-foreground">Select items from your closet</p>
              </div>

              <div className="p-4 max-h-[50vh] overflow-y-auto">
                {closetItems.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center">
                      <span className="text-2xl">👔</span>
                    </div>
                    <p className="text-sm text-foreground font-medium mb-1">No Items in Closet</p>
                    <p className="text-xs text-muted-foreground">
                      Add items to your closet first to enter challenges
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {closetItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedItems(prev => 
                          prev.includes(item.id) 
                            ? prev.filter(id => id !== item.id)
                            : [...prev, item.id]
                        )}
                        className={cn(
                          "aspect-square rounded-lg overflow-hidden border-2 transition-all",
                          selectedItems.includes(item.id)
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-transparent"
                        )}
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center text-2xl">
                            👔
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption (optional)"
                  className="w-full p-3 rounded-xl bg-muted text-foreground placeholder:text-muted-foreground border-0 resize-none"
                  rows={2}
                />
              </div>

              <div className="p-4 border-t border-border flex gap-2">
                <Button variant="outline" onClick={() => setShowSubmit(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitEntry} 
                  className="flex-1"
                  disabled={selectedItems.length === 0}
                >
                  Submit Entry
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
